import React from "react";
import {
  calculateTotalSlides,
  getItemIndexForSlide,
} from "../helpers/templateUtils";

/**
 * Custom hook that manages the slide index and the auto-advance timer.
 *
 * @param {Array}   state       - Normalised list of items (images/videos)
 * @param {boolean} play        - Whether auto-advance is active
 * @param {object}  catalogData - Collection metadata (public) or folderItem (private)
 * @param {object}  folderItem  - Folder metadata (private player)
 * @param {boolean} isPublic    - true for public shared-link mode
 * @param {object}  carouselRef - React ref to the <Carousel> component
 */
export const useSlideshow = (
  state,
  play,
  catalogData,
  folderItem,
  isPublic,
  carouselRef
) => {
  const [slideIndex, setSlideIndex] = React.useState(0);

  // ------------------------------------------------------------------
  // Reset slideIndex to 0 when state changes
  // ------------------------------------------------------------------
  React.useEffect(() => {
    if (state.length > 0) {
      setSlideIndex(0);
      if (carouselRef.current) {
        carouselRef.current.moveTo(0);
      }
    }
  }, [state.length, carouselRef]);

  // ------------------------------------------------------------------
  // When play starts, ensure we're at index 0
  // ------------------------------------------------------------------
  React.useEffect(() => {
    if (play && state.length > 0) {
      setSlideIndex(0);
      if (carouselRef.current) {
        carouselRef.current.moveTo(0);
      }
    }
  }, [play, state.length, carouselRef]);

  // ------------------------------------------------------------------
  // Auto-advance timer
  // ------------------------------------------------------------------
  React.useEffect(() => {
    if (!play || state.length === 0) {
      return;
    }

    const totalSlides = calculateTotalSlides(state);
    if (totalSlides === 0) {
      return;
    }

    // Find the item that starts this slide
    let itemIdx = getItemIndexForSlide(state, slideIndex);

    // Ensure itemIdx is within bounds
    if (itemIdx >= state.length) {
      itemIdx = 0;
    }

    // Get duration from the current item, fallback to folder/catalog default, then 5000ms
    const currentItem = state[itemIdx];
    const duration =
      currentItem?.timePerSlide ||
      (isPublic ? catalogData?.timePerSlide : folderItem?.timePerSlide) ||
      5000;

    // Ensure duration is a valid positive number
    const validDuration = Math.max(1000, duration); // Minimum 1 second

    const timer = setTimeout(() => {
      if (carouselRef.current && play && state.length > 0) {
        const total = calculateTotalSlides(state);
        if (total > 0) {
          const nextIndex = (slideIndex + 1) % total;
          setSlideIndex(nextIndex);
          if (carouselRef.current.moveTo) {
            carouselRef.current.moveTo(nextIndex);
          }
        }
      }
    }, validDuration);

    return () => clearTimeout(timer);
  }, [play, slideIndex, state, catalogData, folderItem, isPublic, carouselRef]);

  // ------------------------------------------------------------------
  // Slide bounds checking
  // ------------------------------------------------------------------
  React.useEffect(() => {
    if (state.length === 0 && slideIndex !== 0) {
      setSlideIndex(0);
    } else if (state.length > 0) {
      const totalSlides = calculateTotalSlides(state);
      if (slideIndex >= totalSlides) {
        setSlideIndex(0);
      }
    }
  }, [slideIndex, state]);

  return { slideIndex, setSlideIndex };
};
